
import { supabase } from "@/integrations/supabase/client";
import { CreateTechniqueInput } from "./userTechniqueService";
import { Technique } from "@/types/technique";
import { transformTechniqueFromDB } from "./types";

// Check if user is an admin
export async function checkIfUserIsAdmin(): Promise<boolean> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      return false;
    }

    // Use the standardized admin check function
    const { data, error } = await supabase
      .rpc('check_admin_status', {
        input_user_id: userData.user.id
      });
      
    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
    
    return !!data;
  } catch (error: unknown) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

// Create an official technique as admin
export async function createOfficialTechnique(input: CreateTechniqueInput): Promise<Technique> {
  try {
    // First, check if user is authenticated and an admin
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error('You must be logged in to create techniques');
    }
    
    // Check if user is an admin
    const isAdmin = await checkIfUserIsAdmin();
    if (!isAdmin) {
      throw new Error('Only administrators can create official techniques');
    }
    
    // Create slug from name
    const slug = input.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    // Find a valid position from the positions table if not provided
    let positionId = input.positionId;
    
    if (!positionId) {
      const { data: positionsData, error: positionsError } = await supabase
        .from('positions')
        .select('id')
        .limit(1);
        
      if (positionsError) {
        console.error('Error fetching positions:', positionsError);
        throw new Error(`Failed to fetch valid position: ${positionsError.message}`);
      }
      
      // Use the first position ID from the database or generate a random UUID as fallback
      positionId = positionsData && positionsData.length > 0 
        ? positionsData[0].id 
        : crypto.randomUUID();
    }
    
    // Insert the technique as an official (non-user-created) technique
    const { data: technique, error } = await supabase
      .from('techniques')
      .insert({
        name: input.name,
        description: input.description,
        position_id: positionId,
        technique_position: input.techniquePosition,
        technique_type: input.techniqueType,
        difficulty: input.difficulty,
        image_url: input.imageUrl || null,
        video_url: input.videoUrl || null,
        key_points: input.keyPoints,
        common_mistakes: input.commonMistakes,
        category: input.category || null,
        subcategory: input.subcategory || null,
        slug,
        is_user_created: false, // Mark as official technique
        created_by_user_id: userData.user.id, // Still track who created it
        is_private: false // Official techniques are always public
      })
      .select()
      .single();
      
    if (error) {
      console.error('Error creating official technique:', error);
      throw new Error(`Failed to create technique: ${error.message}`);
    }
    
    return transformTechniqueFromDB(technique);
    
  } catch (error: unknown) {
    console.error('Unexpected error in createOfficialTechnique:', error);
    throw error instanceof Error
      ? error
      : new Error('An unexpected error occurred while creating the technique');
  }
}
